import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, CardBody, CardHeader, Row, Col, Button, Spinner, Alert, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import usePermission from '../Hooks/usePermission';
import PermissionGuard from '../components/Common/PermissionGuard';
import {
  ESCALATION_CREATE, ESCALATION_UPDATE, ESCALATION_DELETE,
} from '../constants/permissions';
import {
  fetchEscalationPolicies,
  deleteEscalationPolicy,
  toggleEscalationPolicy,
  clearEscalationMessages,
} from '../store/escalation/actions';
import EscalationPolicyForm from './EscalationPolicyForm';

const TARGET_TYPE_LABELS = (t) => ({
  WORK_ORDER: t('shared.target_type_work_order', 'Work Order'),
  SERVICE_REQUEST: t('shared.target_type_service_request', 'Service Request'),
  ASSET: t('shared.target_type_asset', 'Asset'),
});

const TARGET_TYPE_COLORS = {
  WORK_ORDER: 'primary',
  SERVICE_REQUEST: 'info',
  ASSET: 'warning',
};

const CONDITION_TYPE_LABELS = (t) => ({
  OVERDUE: t('shared.condition_overdue', 'Overdue'),
  TIME_IN_STATUS: t('shared.condition_time_in_status', 'Time in Status'),
  PRIORITY: t('shared.condition_priority', 'Priority'),
  ASSET_CRITICALITY: t('shared.condition_asset_criticality', 'Asset Criticality'),
  ASSET_DOWNTIME: t('shared.condition_asset_downtime', 'Asset Downtime'),
});

const EscalationSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const { policies, policiesLoading, policiesError, saving, saveError, saveSuccess } =
    useSelector((state) => state.Escalation);

  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [toggleModal, setToggleModal] = useState(null);

  useEffect(() => {
    document.title = `${t('escalation.title', 'Escalation Policies')} | Arcmedix`;
    dispatch(fetchEscalationPolicies());
  }, [dispatch, t]);

  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => dispatch(clearEscalationMessages()), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError, dispatch]);

  const handleCreate = useCallback(() => {
    setEditingPolicy(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((policy) => {
    setEditingPolicy(policy);
    setShowForm(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setEditingPolicy(null);
    dispatch(fetchEscalationPolicies());
  }, [dispatch]);

  const handleDelete = useCallback(() => {
    if (deleteModal) {
      dispatch(deleteEscalationPolicy(deleteModal.id, () => setDeleteModal(null)));
    }
  }, [deleteModal, dispatch]);

  const handleToggle = useCallback(() => {
    if (toggleModal) {
      dispatch(toggleEscalationPolicy(toggleModal.id, () => {
        setToggleModal(null);
      }));
    }
  }, [toggleModal, dispatch]);

  const translateEnum = (value) =>
    value ? t(`shared.${String(value).toLowerCase()}`, value) : value;

  const formatCondition = (condition) => {
    const type = CONDITION_TYPE_LABELS(t)[condition.conditionType] || condition.conditionType;
    switch (condition.conditionType) {
      case 'PRIORITY':
        return `${type}: ${translateEnum(condition.urgencyLevel)}`;
      case 'ASSET_CRITICALITY':
        return `${type}: ${translateEnum(condition.assetCriticality)}`;
      case 'TIME_IN_STATUS':
        return `${type}: ${translateEnum(condition.targetStatus)} > ${condition.thresholdHours}h`;
      case 'ASSET_DOWNTIME':
        return `${type} > ${condition.thresholdHours}h`;
      case 'OVERDUE':
        return type;
      default:
        return type;
    }
  };

  if (showForm) {
    return <EscalationPolicyForm policy={editingPolicy} onClose={handleFormClose} />;
  }

  return (
    <div className="page-content mt-4">
      <div className="container-fluid">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="escalation:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={handleCreate}
              title={t('escalation.create_policy', 'Create Policy')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t('escalation.create_policy', 'Create Policy')}
            </button>
          </PermissionGuard>
        </motion.div>

        {saveSuccess && <Alert color="success" className="mb-3">{t(saveSuccess, saveSuccess)}</Alert>}
        {saveError && <Alert color="danger" className="mb-3">{saveError}</Alert>}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">{t('escalation.title', 'Escalation Policies')}</h3>
          </CardHeader>
          <CardBody>
            {policiesLoading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : policiesError ? (
              <Alert color="danger">{policiesError}</Alert>
            ) : policies.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FeatherIcon icon="alert-triangle" size={48} className="mb-3" />
                <p>{t('escalation.no_policies', 'No escalation policies configured yet.')}</p>
              </div>
            ) : (
              policies.map((policy, idx) => (
                <div key={policy.id} className={`py-3 ${idx < policies.length - 1 ? 'border-bottom' : ''}`}>
                  <Row className="align-items-center">
                    <Col>
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="mb-0 fw-bold me-2">{policy.name}</h6>
                        <Badge color={TARGET_TYPE_COLORS[policy.targetType] || 'secondary'} className="me-2">
                          {TARGET_TYPE_LABELS(t)[policy.targetType] || policy.targetType}
                        </Badge>
                        <Badge color={policy.enabled ? 'success' : 'secondary'}>
                          {policy.enabled
                            ? t('escalation.enabled', 'Enabled')
                            : t('escalation.disabled', 'Disabled')}
                        </Badge>
                      </div>
                      {policy.description && (
                        <p className="text-muted small mb-2">{policy.description}</p>
                      )}
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {policy.conditions?.map((cond, cidx) => (
                          <Badge key={cidx} color="light" className="text-dark border">
                            {formatCondition(cond)}
                          </Badge>
                        ))}
                      </div>
                      <small className="text-muted">
                        {policy.levels?.length || 0} {t('escalation.levels_count', 'escalation level(s)')}
                      </small>
                    </Col>
                    <Col xs="auto" className="d-flex gap-2">
                      {hasPermission(ESCALATION_UPDATE) && (
                        <>
                          <Button
                            color={policy.enabled ? 'outline-secondary' : 'outline-success'}
                            size="sm"
                            onClick={() => setToggleModal(policy)}
                            disabled={saving}
                            title={policy.enabled ? t('escalation.disable', 'Disable') : t('escalation.enable', 'Enable')}
                          >
                            <FeatherIcon icon={policy.enabled ? 'toggle-right' : 'toggle-left'} size={16} />
                          </Button>
                          <Button color="outline-primary" size="sm" onClick={() => handleEdit(policy)} title={t('shared.edit', 'Edit')}>
                            <FeatherIcon icon="edit-2" size={16} />
                          </Button>
                        </>
                      )}
                      {hasPermission(ESCALATION_DELETE) && (
                        <Button color="outline-danger" size="sm" onClick={() => setDeleteModal(policy)} title={t('shared.delete', 'Delete')}>
                          <FeatherIcon icon="trash-2" size={16} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                </div>
              ))
            )}
          </CardBody>
        </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)} centered>
        <ModalHeader toggle={() => setDeleteModal(null)}>
          {t('escalation.confirm_delete_title', 'Delete Policy')}
        </ModalHeader>
        <ModalBody>
          {t('escalation.confirm_delete', 'Are you sure you want to delete this escalation policy?')}
          <p className="fw-bold mt-2">{deleteModal?.name}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(null)}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={saving}>
            {saving ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Toggle Confirmation Modal */}
      <Modal isOpen={!!toggleModal} toggle={() => setToggleModal(null)} centered>
        <ModalHeader toggle={() => setToggleModal(null)}>
          {toggleModal?.enabled
            ? t('escalation.disable_policy', 'Disable Policy')
            : t('escalation.enable_policy', 'Enable Policy')}
        </ModalHeader>
        <ModalBody>
          <p>
            {toggleModal?.enabled
              ? t('escalation.confirm_disable', 'This policy will stop evaluating new escalations.')
              : t('escalation.confirm_enable', 'This policy will start evaluating escalations for new items.')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setToggleModal(null)}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button
            color={toggleModal?.enabled ? 'warning' : 'success'}
            onClick={handleToggle}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : (toggleModal?.enabled
              ? t('escalation.disable', 'Disable')
              : t('escalation.enable', 'Enable'))}
          </Button>
        </ModalFooter>
      </Modal>
      </div>
    </div>
  );
};

export default EscalationSettings;
